import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Copy, CheckCircle2 } from "lucide-react";

interface SharePointSiteInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SharePointSiteInfoModal({ open, onOpenChange }: SharePointSiteInfoModalProps) {
  const [siteUrl, setSiteUrl] = useState("https://greenvisionbv.sharepoint.com/sites/Projecten");
  const [loading, setLoading] = useState(false);
  const [siteInfo, setSiteInfo] = useState<any>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleGetSiteInfo = async () => {
    setLoading(true);
    setSiteInfo(null);

    try {
      const { data, error } = await supabase.functions.invoke('sharepoint-get-site-info', {
        body: { siteUrl },
      });

      if (error) throw error;

      if (data.success) {
        setSiteInfo(data);
        toast.success("SharePoint site informatie opgehaald!");
      } else {
        throw new Error(data.error || 'Failed to get site info');
      }
    } catch (error: any) {
      console.error('Error getting site info:', error);
      toast.error(`Fout bij ophalen site info: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    toast.success(`${fieldName} gekopieerd!`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>SharePoint Site Informatie Ophalen</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="siteUrl">SharePoint Site URL</Label>
            <div className="flex gap-2 mt-2">
              <Input
                id="siteUrl"
                value={siteUrl}
                onChange={(e) => setSiteUrl(e.target.value)}
                placeholder="https://greenvisionbv.sharepoint.com/sites/Projecten"
                className="flex-1"
              />
              <Button onClick={handleGetSiteInfo} disabled={loading || !siteUrl}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Ophalen...
                  </>
                ) : (
                  'Ophalen'
                )}
              </Button>
            </div>
          </div>

          {siteInfo && (
            <div className="space-y-4 mt-6">
              <div className="p-4 bg-muted rounded-lg space-y-3">
                <h3 className="font-semibold text-lg">Site Informatie</h3>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Site Naam:</Label>
                    <span className="text-sm">{siteInfo.siteName}</span>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-sm font-medium">Site ID:</Label>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(siteInfo.siteId, 'Site ID')}
                      >
                        {copiedField === 'Site ID' ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <code className="text-xs bg-background p-2 rounded block break-all">
                      {siteInfo.siteId}
                    </code>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-sm font-medium">Drive ID:</Label>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(siteInfo.driveId, 'Drive ID')}
                      >
                        {copiedField === 'Drive ID' ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <code className="text-xs bg-background p-2 rounded block break-all">
                      {siteInfo.driveId}
                    </code>
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Drive Web URL:</Label>
                    <a 
                      href={siteInfo.driveWebUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline block break-all"
                    >
                      {siteInfo.driveWebUrl}
                    </a>
                  </div>
                </div>
              </div>

              {siteInfo.allDrives && siteInfo.allDrives.length > 1 && (
                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-semibold mb-2">Alle Drives op deze Site:</h4>
                  <ul className="space-y-2 text-sm">
                    {siteInfo.allDrives.map((drive: any) => (
                      <li key={drive.id} className="pl-2 border-l-2 border-primary/30">
                        <div className="font-medium">{drive.name}</div>
                        <code className="text-xs opacity-70">{drive.id}</code>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <h4 className="font-semibold mb-2 text-yellow-800 dark:text-yellow-200">
                  Volgende stappen:
                </h4>
                <ol className="text-sm space-y-1 list-decimal list-inside text-yellow-700 dark:text-yellow-300">
                  <li>Kopieer de Site ID en Drive ID hierboven</li>
                  <li>Ga naar <a href="https://supabase.com/dashboard/project/fxlorumfwzrdwiftbalo/settings/functions" target="_blank" rel="noopener noreferrer" className="underline">Supabase Functions Settings</a></li>
                  <li>Update de secrets <code>SHAREPOINT_SITE_ID</code> en <code>SHAREPOINT_DRIVE_ID</code></li>
                  <li>Klik daarna op "Reset SharePoint Config" in de project lijst</li>
                  <li>Run vervolgens "Sync SharePoint" opnieuw</li>
                </ol>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
